/**
 * PhpNativeForm — render an Add/Edit form 100% natively in React from
 * the spec captured in `php-page-specs.generated.json`.
 *
 * No iframe, no proxy: the form posts straight to the Node CRUD endpoints
 *   POST  /api/admin/php-mirror/:table/rows
 *   PATCH /api/admin/php-mirror/:table/row/:id
 *
 * The spec carries everything we need (label, name, type, required,
 * placeholder, hint, optional select options inferred from list-page
 * columns). Whatever the PHP form has, we reproduce here in Ant Design.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  Alert, Button, Checkbox, DatePicker, Form, Input, InputNumber, Modal, Radio, Select,
  Space, Switch, Tabs, Tag, message,
} from 'antd';
import dayjs, { Dayjs } from 'dayjs';
import { api } from '../../api/client';

export interface FormFieldSpec {
  name: string;
  type: string;          // 'text' | 'textarea' | 'select' | 'radio' | 'checkbox' | 'image' | 'file' | 'date' | 'number' | 'switch' | …
  required: boolean;
  label: string;
  placeholder?: string;
  hint?: string;
  options?: { value: string; label: string }[];
  /**
   * Default value captured by the crawler from the live PHP form (populated
   * by the PHP controller itself — e.g. the "基础配置" form arrives with
   * `value="7.15"` for 当前汇率, which is the current system exchange rate).
   * Used as a fallback when no explicit `initialValues` prop is supplied,
   * so `kind:"form"` config pages render with real values instead of blanks.
   */
  value?: string;
  /** Field is read-only (rendered disabled, never submitted). */
  readonly?: boolean;
  /**
   * Field had no `name` attribute in the original PHP HTML — it's just a
   * live-stat display that sits inside the form (e.g. 可捐赠FP统计 shows
   * 分叉铸造合约 / 可捐赠FP数 next to the 捐赠数量 input). We still render
   * it so the operator can see the same context PHP shows, but we skip
   * it entirely from the submit payload.
   */
  display_only?: boolean;
}
export interface FormTabSpec { title: string; fields: FormFieldSpec[] }
export interface FormSpec { submit_url: string | null; tabs: FormTabSpec[] }

interface Props {
  table: string;
  /** Form spec (use spec.add_form for Add, spec.form for kind:'form' configs). */
  form: FormSpec;
  /** Pass when editing — pre-fills and switches to PATCH. */
  rowId?: string;
  /** Optional initial values (for edit). */
  initialValues?: Record<string, unknown>;
  /** Called after a successful save. */
  onSaved?: (id: string) => void;
  /** Hide submit button (when caller wants its own footer). */
  hideSubmit?: boolean;
  /**
   * When set, submit goes to POST /:table/row/:id/php-action with this
   * `url_path` instead of PATCHing the mirror directly. Use for action
   * buttons (重置密码 / 冻结 / 限额 / 红心) where the legacy PHP backend
   * owns the business logic and we just want the mirror to refresh.
   */
  phpAction?: { url_path: string };
}

interface SaveResp { table: string; id: string; row: Record<string, unknown> }

function valueToFormInput(type: string, raw: unknown): unknown {
  if (raw === null || raw === undefined || raw === '') return undefined;
  if (type === 'checkbox') {
    if (Array.isArray(raw)) return raw.map(String);
    if (typeof raw === 'string') {
      try { const j = JSON.parse(raw); if (Array.isArray(j)) return j.map(String); } catch { /* ignore */ }
      return raw.split(',').map((s) => s.trim()).filter(Boolean);
    }
    return [String(raw)];
  }
  if (type === 'date' || type === 'datetime') {
    const n = Number(raw);
    if (!Number.isNaN(n) && n > 1_000_000_000) return dayjs.unix(n);
    const d = dayjs(String(raw));
    return d.isValid() ? d : undefined;
  }
  if (type === 'switch') return String(raw) === '1' || raw === true;
  return String(raw);
}

function formToWire(type: string, v: unknown): unknown {
  if (v === undefined || v === null || v === '') return null;
  if (type === 'switch') return v ? '1' : '0';
  if (type === 'checkbox' && Array.isArray(v)) return JSON.stringify(v);
  if (type === 'date' || type === 'datetime') {
    const d = v as Dayjs;
    return d?.unix ? String(d.unix()) : String(v);
  }
  if (typeof v === 'number') return String(v);
  return v;
}

export function PhpNativeForm({ table, form, rowId, initialValues, onSaved, hideSubmit, phpAction }: Props) {
  const [antForm] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const [activeTab, setActiveTab] = useState(form.tabs[0]?.title ?? '');

  // Map mirror row → form fields.
  //
  // Precedence (per field):
  //   1. caller-supplied initialValues[f.name] (e.g. the row being edited)
  //   2. the crawler-captured default baked into the spec (f.value)
  //
  // Without (2), `kind:"form"` config pages like 基础配置 / 币价修改 / UOC 充值
  // render every input blank even though PHP serves them pre-filled with
  // the live system values. Using f.value restores parity with PHP.
  useEffect(() => {
    const flat: Record<string, unknown> = {};
    let touched = false;
    for (const tab of form.tabs) {
      for (const f of tab.fields) {
        const fromInitial = initialValues ? initialValues[f.name] : undefined;
        const raw = (fromInitial === undefined || fromInitial === null || fromInitial === '')
          ? f.value
          : fromInitial;
        const mapped = valueToFormInput(f.type, raw);
        if (mapped !== undefined) {
          flat[f.name] = mapped;
          touched = true;
        }
      }
    }
    if (touched) {
      antForm.setFieldsValue(flat);
    } else {
      antForm.resetFields();
    }
  }, [antForm, form, initialValues]);

  const fieldByName = useMemo(() => {
    const m = new Map<string, FormFieldSpec>();
    for (const t of form.tabs) for (const f of t.fields) m.set(f.name, f);
    return m;
  }, [form]);

  async function onFinish(values: Record<string, unknown>): Promise<void> {
    const payload: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(values)) {
      const f = fieldByName.get(k);
      // Skip display-only fields — they came from PHP without a name attr
      // and the backend does not expect them in the submit payload.
      if (f?.display_only) continue;
      payload[k] = formToWire(f?.type ?? 'text', v);
    }
    setSubmitting(true);
    try {
      if (phpAction && rowId) {
        const r = await api.post<{ php: { msg: string }; refreshed: boolean }>(
          `/api/admin/php-mirror/${encodeURIComponent(table)}/row/${encodeURIComponent(rowId)}/php-action`,
          { url_path: phpAction.url_path, payload },
        );
        message.success(r.php?.msg || '操作成功');
        onSaved?.(rowId);
      } else {
        const resp = rowId
          ? await api.patch<SaveResp>(`/api/admin/php-mirror/${encodeURIComponent(table)}/row/${encodeURIComponent(rowId)}`, { fields: payload })
          : await api.post<SaveResp>(`/api/admin/php-mirror/${encodeURIComponent(table)}/rows`, { fields: payload });
        message.success(rowId ? '已保存' : '已创建');
        onSaved?.(resp.id);
      }
    } catch (e) {
      message.error(e instanceof Error ? e.message : String(e));
    } finally {
      setSubmitting(false);
    }
  }

  function renderField(f: FormFieldSpec): React.ReactNode {
    const ro = !!(f.readonly || f.display_only);
    const common = { placeholder: f.placeholder, allowClear: !ro, disabled: ro } as const;
    switch (f.type) {
      case 'textarea':
        return <Input.TextArea {...common} autoSize={{ minRows: 3, maxRows: 8 }} />;
      case 'number':
        return <InputNumber style={{ width: '100%' }} placeholder={f.placeholder} disabled={ro} />;
      case 'select':
        return (
          <Select
            placeholder={f.placeholder}
            allowClear={!ro}
            disabled={ro}
            showSearch
            optionFilterProp="label"
            options={f.options ?? []}
          />
        );
      case 'radio':
        return (
          <Radio.Group disabled={ro}>
            {(f.options ?? []).map((o) => <Radio key={o.value} value={o.value}>{o.label}</Radio>)}
          </Radio.Group>
        );
      case 'checkbox':
        return (
          <Checkbox.Group disabled={ro}>
            {(f.options ?? []).map((o) => <Checkbox key={o.value} value={o.value}>{o.label}</Checkbox>)}
          </Checkbox.Group>
        );
      case 'switch':
        return <Switch checkedChildren="启用" unCheckedChildren="禁用" disabled={ro} />;
      case 'date':
      case 'datetime':
        return <DatePicker showTime={f.type === 'datetime'} style={{ width: '100%' }} disabled={ro} />;
      case 'image':
      case 'file': {
        // The PHP backend uploads files; here we accept a URL OR a single file.
        // For native parity we fall back to "paste a URL" (no upload backend
        // is mounted yet on Node). This still beats the iframe option.
        return <Input {...common} placeholder={f.placeholder ?? '请输入资源 URL'} />;
      }
      default:
        return <Input {...common} />;
    }
  }

  const formItems = (fields: FormFieldSpec[]) => (
    <>
      {fields.map((f) => {
        const ro = !!(f.readonly || f.display_only);
        const label = ro
          ? <Space size={6}>{f.label || f.name}<Tag color="default" style={{ marginInlineStart: 0 }}>只读</Tag></Space>
          : (f.label || f.name);
        return (
          <Form.Item
            key={f.name}
            name={f.name}
            label={label}
            tooltip={f.hint}
            valuePropName={f.type === 'switch' ? 'checked' : 'value'}
            rules={f.required && !ro ? [{ required: true, message: `请填写${f.label || f.name}` }] : []}
          >
            {renderField(f)}
          </Form.Item>
        );
      })}
    </>
  );

  return (
    <Form
      form={antForm}
      layout="vertical"
      onFinish={onFinish}
      requiredMark="optional"
    >
      {form.tabs.length === 0 && (
        <Alert type="warning" showIcon message="该表单没有提取到字段。" />
      )}
      {form.tabs.length === 1 ? (
        formItems(form.tabs[0].fields)
      ) : (
        <Tabs
          activeKey={activeTab}
          onChange={setActiveTab}
          items={form.tabs.map((t) => ({
            key: t.title,
            label: t.title,
            forceRender: true,
            children: formItems(t.fields),
          }))}
        />
      )}
      {!hideSubmit && (
        <Space>
          <Button type="primary" htmlType="submit" loading={submitting}>
            {rowId ? '保存修改' : '创建'}
          </Button>
          {rowId && <Tag color="default">id={rowId}</Tag>}
        </Space>
      )}
    </Form>
  );
}

/** Confirm-and-delete helper used by row action columns. */
export function confirmAndDelete(table: string, id: string, onDone: () => void): void {
  Modal.confirm({
    title: `确认删除 id=${id}？`,
    content: '此操作直接作用于 Postgres 镜像表，不可恢复。',
    okType: 'danger',
    onOk: async () => {
      try {
        await api.del(`/api/admin/php-mirror/${encodeURIComponent(table)}/row/${encodeURIComponent(id)}`);
        message.success('已删除');
        onDone();
      } catch (e) {
        message.error(e instanceof Error ? e.message : String(e));
      }
    },
  });
}

